import React from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClone, faCopy, faEye, faLocationDot, faPen, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import { ActionsDropdown } from 'components/common/ActionsDropdown/ActionsDropdown';
import { ActionsDropdownOption } from 'components/common/ActionsDropdown/ActionsDropdownOption';
import { EmailTemplate } from '../types';
import { formatDate, templateCategoryLabel } from '../utils';
import { TemplateThumbnail } from '../UI/TemplateThumbnail/TemplateThumbnail';
import styles from './TemplateCard.module.scss';

interface TemplateCardProps {
    template: EmailTemplate;
    locationName?: string;
    selected?: boolean;
    compact?: boolean;
    onPreview: () => void;
    onUse: () => void;
    onEdit?: () => void;
    onCopy?: () => void;
    onDuplicate?: () => void;
    onDelete?: () => void;
}

export const TemplateCard: React.FC<TemplateCardProps> = ({
    template,
    locationName,
    selected,
    compact,
    onPreview,
    onUse,
    onEdit,
    onCopy,
    onDuplicate,
    onDelete
}) => {
    const isLocationTemplate = template.source === 'location';

    const options = [
        onEdit && { id: 'edit', label: 'Edit', icon: faPen, onClick: onEdit },
        onCopy && { id: 'copy', label: 'Copy to locations', icon: faClone, onClick: onCopy },
        onDuplicate && { id: 'duplicate', label: 'Duplicate', icon: faCopy, onClick: onDuplicate },
        onDelete && { id: 'delete', label: 'Delete', icon: faTrashCan, onClick: onDelete }
    ].filter(Boolean) as Array<ActionsDropdownOption>;

    return (
        <article className={clsx(styles.root, { [styles.selected]: selected, [styles.compact]: compact })}>
            <div
                className={styles.media}
                onClick={compact ? onUse : undefined}
            >
                <TemplateThumbnail html={template.html} />

                {!compact && (
                    <div className={styles.overlay}>
                        <Button
                            size="small"
                            variant="outlined"
                            leftIcon={faEye}
                            label="Preview"
                            onClick={onPreview}
                        />
                        <Button
                            size="small"
                            variant="black"
                            label={isLocationTemplate ? 'Use in campaign' : 'Add to locations'}
                            onClick={onUse}
                        />
                    </div>
                )}

                {selected && <span className={styles.selectedBadge}>Selected</span>}
            </div>

            <div className={styles.body}>
                <div className={styles.text}>
                    <h4 className={styles.name}>{template.name}</h4>
                    {/* Location templates show their owner; categories only exist in the Cuddles gallery. */}
                    {isLocationTemplate ? (
                        <span className={styles.meta}>
                            <FontAwesomeIcon icon={faLocationDot} /> {locationName}
                            {!compact && template.updatedAt && ` · ${formatDate(template.updatedAt)}`}
                        </span>
                    ) : (
                        <span className={styles.meta}>{templateCategoryLabel(template.category)}</span>
                    )}
                </div>

                {compact ? (
                    <button
                        type="button"
                        className={styles.previewLink}
                        onClick={onPreview}
                    >
                        Preview
                    </button>
                ) : (
                    options.length > 0 && (
                        <ActionsDropdown
                            direction="down"
                            options={options}
                        />
                    )
                )}
            </div>
        </article>
    );
};

export default TemplateCard;
